import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';

import { updateSentiment as updateSentimentRequest } from '@/apis/social-listening';
import { useSocialListening } from '@/app/socialListening/composables/useSocialListening';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import { useSocialListeningSentimentStore } from '@/stores/social-listening-sentiment';
import { useSocialListeningTopPostsStore } from '@/stores/social-listening-top-posts';
import { useSocialListeningTopicsStore } from '@/stores/social-listening-topics';
import { useSocialListeningTrendsStore } from '@/stores/social-listening-trends';

interface OrganizationSentimentPost {
  source: string;
  sourceId: string;
  sentimentLabel?: string;
  organizationSentiment?: unknown;
}

function getSourceAndId(post: OrganizationSentimentPost) {
  return `${post.source}:${post.sourceId}`;
}

export function useOrganizationSentiment() {
  const { organizationId, isTrendsPage, isComparisonPage } = useSocialListening();
  const sentimentStore = useSocialListeningSentimentStore();
  const topPostsStore = useSocialListeningTopPostsStore();
  const { addToast } = useToast({ life: 5000 });

  function isPending(post: OrganizationSentimentPost) {
    return sentimentStore.isPending(getSourceAndId(post));
  }

  async function refetchStats() {
    try {
      if (isTrendsPage.value) {
        await useSocialListeningTrendsStore().getSentimentTrend();
      } else if (isComparisonPage.value) {
        await useSocialListeningComparisonsStore().fetchTopicsStats();
      } else {
        const topicsStore = useSocialListeningTopicsStore();
        const { baseSearchFilters } = topicsStore;
        await Promise.all([
          topicsStore.fetchTopicStats({ overrideFilters: { searchFilters: baseSearchFilters } }),
          topicsStore.getSentimentTrend(baseSearchFilters),
          topicsStore.getNetSentimentTimeSeries(baseSearchFilters),
        ]);
      }
    } catch {
      // Refetching stats is a secondary side effect of updating sentiment;
      // a failure here must not surface to the caller or block the primary flow.
    }
  }

  async function updateSentiment(post: OrganizationSentimentPost, label: string) {
    const sourceAndId = getSourceAndId(post);
    sentimentStore.setPendingState(sourceAndId, true);
    try {
      const {
        data: {
          data: { override },
        },
      } = await updateSentimentRequest({
        organizationId: organizationId.value,
        sourceAndId,
        sentimentLabel: label,
      });
      post.sentimentLabel = label;
      post.organizationSentiment = override;
      topPostsStore.updateSentimentAcrossTopPosts(post.source, post.sourceId, {
        sentimentLabel: label,
        organizationSentiment: override,
      });
      void refetchStats();
      addToast({
        severity: 'success',
        summary: 'Sentiment Update',
        message: 'Sentiment successfully adjusted',
      });
      return true;
    } catch {
      addToast({
        severity: 'error',
        summary: 'Sentiment Update',
        message: 'Something went wrong',
      });
      return false;
    } finally {
      sentimentStore.setPendingState(sourceAndId, false);
    }
  }

  return { updateSentiment, isPending };
}
